import { useTranslation } from "react-i18next"
import { observer } from "mobx-react-lite"
import { useStore } from "../../../contexts/store"
import { ConversationItem } from "../../../shared/src/types/Miscs"

import UserInfoAvatar from "../../Elements/UserInfoAvatar"
import UxIcon from "../../Elements/UxIcon"

import { Badge, BadgeProps, Box, Button, Divider, IconButton, Pagination, styled } from "@mui/material"
import Link from "../../Elements/Link"
import React from "react"
import PrettyMarkdownBox from "../../PrettyMarkdownInput/PrettyMarkdownBox"
import { md2html, md2text } from "../../PrettyMarkdownInput/converter"


const ConversationsPreview = ({podId}: {podId: string}) => {
  const { t } = useTranslation()
  const { podStore, sessionStore, uiStore } = useStore()
  const [ teaserSlotCounter, setTeaserSlotCounter ] = React.useState(1)
  const deviceWidth = uiStore.deviceWidth
  const pod = podStore.pod

  const nofPreviews = 6
  const minNofTeasers = 2
  const session = sessionStore.session

  if (!session) return null
  if (!pod) return null

  const userId = session.user.userId

  // Get two lists of conversations: ones that the user is involved in, and ones that she is not, as well as a list of comments that are listed in either of the two lists
  const myConversations:ConversationItem[] = []
  const otherConversations:ConversationItem[] = []
  const unansweredComments:ConversationItem[] = []

  const conversationIds:{[threadId:string]:true} = {}
  const conversationCommentIds:{[interactionId:string]:true} = {}

  const threads = pod.content.threads

  if (threads) {
    for (const threadId in threads) {
      const thread = threads[threadId]
      const baseInteraction = pod.getInteractionFromThreadId(thread.threadId)

      // if there is no baseInteraction thread was probably deleted
      if (!baseInteraction) continue
      // do not include conversations tied to reading questions
      if (baseInteraction.interactionType === "readingQuestion") continue

      const conversationItem = podStore.createConversationItem(thread, baseInteraction)

      if (!conversationItem) continue

      if (conversationIds[conversationItem.interactionId]) continue

      conversationCommentIds[baseInteraction?.interactionId] = true
      conversationIds[conversationItem.interactionId] = true

      if (baseInteraction.userId === userId) {
        myConversations.push(conversationItem)
      }
      else if (thread.messages.findIndex(m => m.userId === userId)>-1) {
        myConversations.push(conversationItem)
      }
      else {
        otherConversations.push(conversationItem)
      }
    }
  }

  myConversations.sort((a: ConversationItem, b: ConversationItem) => {
    if (a.tLastMessage > b.tLastMessage) return -1
    if (a.tLastMessage < b.tLastMessage) return 1
    return 0
  })

  otherConversations.sort((a: ConversationItem, b: ConversationItem) => {
    if (a.tLastMessage > b.tLastMessage) return -1
    if (a.tLastMessage < b.tLastMessage) return 1
    return 0
  })

  // Get a list of comments by other users that are not answered yet
  Object.keys(pod.content.pdfFiles).forEach(nodeId => {
    Object.keys(pod.content.pdfFiles[nodeId].comments).forEach(interactionId => {

      const comment = pod.content.pdfFiles[nodeId].comments[interactionId]
      if ((comment.userId !== userId) && (!conversationCommentIds[interactionId])) unansweredComments.push({
        nodeId,
        interactionId,
        userId: comment.userId,
        userName: comment.userName || '',
        label: comment.label,
        involvedUsers: [comment.userId],
        replies:0,
        tLastMessage: comment.tModified,
        threadId:`nothread-${interactionId}`,
        lastMessage: comment.label,
        lastMessageUserId: comment.userId,
        lastMessageUserName: comment.userName || '',
        hasUnreadMessages:true,
      })
    })
  })

  unansweredComments.sort((a: ConversationItem, b: ConversationItem) => {
    if (a.tLastMessage > b.tLastMessage) return -1
    if (a.tLastMessage < b.tLastMessage) return 1
    return 0
  })

  const conversationTeasers:ConversationItem[] = []
  for (var i=0; i<Math.max(otherConversations.length, unansweredComments.length); i++) {
    if (otherConversations[i]) conversationTeasers.push(otherConversations[i])
    if (unansweredComments[i]) conversationTeasers.push(unansweredComments[i])
  }

  const changeActiveConversation = (interactionId: string | null) => {
    uiStore.setActiveConversation(podId, interactionId)
    uiStore.setEditedMessageId(null)
  }

  const UserAvatar = ({item}: {item: ConversationItem}) => (
    <UserInfoAvatar key={item.lastMessageUserId} userId={item.lastMessageUserId} podId={podId} sx={{
      fontSize:"16px",
      height: "40px",
      width: "40px"
    }}/>
  )

  const previewConversation = (item:ConversationItem) => {
    return <Link key={item.threadId} noDecoration={true} to={`/pod/${podId}/conversations${item.interactionId ? `/${item.interactionId}` : ""}`} onClick={() => changeActiveConversation(item.interactionId)}>
        <Box sx={{
            alignItems: "center",
            display: "grid",
            gap: "12px",
            gridTemplateColumns: "max-content auto min-content 1px",
            padding: "10px",
            borderRadius:1,
            '&:hover': {
              boxShadow:'0 0 10px 0 #0008',
            }
          }}>
          <UserAvatar item={item}/>
          <Box sx={{
            color: "textcolor.main",
            display: "grid",
            gridTemplateRows: "max-content max-content",
            paddingRight: "25px"
          }}>
            <Box sx={{
                fontWeight: 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
              }}>
              {item.lastMessageUserName}
            </Box>
            <Box sx={{
                fontWeight: 300,
                width:'100%',
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
              }}>
              <div
                className="pretty-markdown-input"
                style={{padding:0, overflow:'hidden', border:'none'}}
                dangerouslySetInnerHTML={{__html: md2html(item.lastMessage, null, ['Bold', 'Emphasis', 'Underline', 'Strikethrough']).replace(/<\/?p>/g, '')}}
              />
            </Box>
          </Box>
          <UnreadBadge variant='dot' badgeContent={Number(item.hasUnreadMessages)} ></UnreadBadge>
      </Box>
    </Link>
  }
//<PrettyMarkdownBox value={item.lastMessage} style={{padding:0, overflow:'hidden'}} whitelist={['Bold', 'Emphasis', 'Underline', 'Strikethrough']} />
  const myConversationsSlots = Math.min(nofPreviews - Math.min(minNofTeasers, conversationTeasers.length), myConversations.length)
  const teaserSlots = nofPreviews - myConversationsSlots
  const nofTeaserPanels = Math.ceil((otherConversations.length + unansweredComments.length+teaserSlots) / teaserSlots)

  return (
    <Box sx={{
      display: "grid",
      gap: "5px",
      margin: "20px",
    }}>
      {myConversations.length === 0 ?
        <Box sx={{
          display: "grid",
          fontSize: "17px",
          fontStyle: "italic",
          fontWeight: 300,
          justifyContent: "center",
          padding: "8px",
          margin: "30px"
        }}>
          {t('No Conversations yet')}
        </Box>
        :
        <>
          { myConversations.slice(0, myConversationsSlots).map(item => previewConversation(item)) }
        </>
      }
      {(conversationTeasers.length) ? <Box sx={{backgroundColor:'#eee', borderRadius:5, mt:1, ml:-2, mr:-2, px:2, pb:1}}>
        <Divider sx={{mt:1}}>{t('otherConversations', {count: conversationTeasers.length})}</Divider>
        <div style={{opacity:.7}}>
        { conversationTeasers.slice(((teaserSlotCounter-1) * teaserSlots), (teaserSlotCounter * teaserSlots)).map(item => previewConversation(item)) }
        { Array.from({ length: Math.max(0, (teaserSlotCounter * teaserSlots) - conversationTeasers.length) }, (_, i) => <Box key={i} sx={{padding: '10px',height:'68px'}}></Box>) }
        </div>
        {(conversationTeasers.length>teaserSlots) &&
        <Box sx={{display:'flex', justifyContent:'center', marginTop:1}}>
          <Pagination size='small' count={nofTeaserPanels-1} page={teaserSlotCounter} onChange={(event: React.ChangeEvent<unknown>, value: number) => { setTeaserSlotCounter(value) }} />
        </Box>
        }
      </Box> : null}
    </Box>
  )
}

export default observer(ConversationsPreview)

const UnreadBadge = styled(Badge)<BadgeProps>(({ theme }) => ({
  '& .MuiBadge-badge': {
    border: `2px solid #f8f8ff`,
    backgroundColor: theme.palette.secondary.main
  }
}))
